import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { AppService } from 'app/app.service';
import { AccordionModule } from 'primeng/accordion';
import { Subscription } from 'rxjs';

interface CacheList {
  id: number;
  key: string;
  value: string;
}

@Component({
    selector: 'app-cache-viewer',
    imports: [
        CommonModule,
        BreadcrumbComponent,
        AccordionModule
    ],
    templateUrl: './cache-viewer.component.html',
    styleUrl: './cache-viewer.component.scss'
})
export class CacheViewerComponent implements OnInit, OnDestroy {

  sub?: Subscription;
  cacheList?: any;
  category?: string;
  theme$ = this.appService.theme$;

  constructor(
    private route: ActivatedRoute,
    private errorLogService: ErrorLogService,
    private breadcrumbService: BreadcrumbService,
    private appService: AppService
  ) { }

  ngOnInit(): void {
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Cache`';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);

    this.sub = this.route.params.subscribe(params => {
      const cacheType = params['id'];

      if (cacheType === 'local') {
        this.loadLocalCache();
      } 
      else if (cacheType === 'session') {
        this.loadSessionCache();
      }
      else if (cacheType === 'error') {
        this.loadErrorLog();
      }
    });
  }

  loadLocalCache(): void {
    this.category = 'Local Storage Cache';
    let cacheList: CacheList[] = [];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key) {
        let value = localStorage.getItem(key) || '';
        if (value) {
          try {
            value = JSON.parse(value);
            cacheList.push({
              id: i,
              key: key,
              value: JSON.stringify(value, null, 2)
            });
          } catch (e) {
            this.errorLogService.logError(`Error parsing local storage cache - ${e}`);
          }
        }
      }
    }
    this.cacheList = cacheList;
  }

  loadSessionCache(): void {
    this.category = 'Session Storage Cache';
    let cacheList: CacheList[] = [];
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key) {
        let value = sessionStorage.getItem(key) || '';
        if (value && key !== 'errorLog') {
          try {
            value = JSON.parse(value);
            cacheList.push({
              id: i,
              key: key,
              value: JSON.stringify(value, null, 2)
            });
          } catch (e) {
            this.errorLogService.logError(`Error parsing session storage cache - ${e}`);
          }
        }
      }
    }
    this.cacheList = cacheList;
  }

  loadErrorLog(): void {
    this.category = 'Error Log';
    let cacheList: CacheList[] = [];
    const errorLogs = sessionStorage.getItem('errorLog');
    if (errorLogs) {
      const logs = errorLogs ? JSON.parse(errorLogs) : [];
      if (logs && logs.length > 0) {
        for (let i = 0; i < logs.length; i++) {
          cacheList.push({
            id: i,
            key: `Error Log ${i + 1}`,
            value: logs[i]
          });
        }
      }
      else {
        cacheList.push({
          id: 0,
          key: 'Error Log',
          value: 'No error logs found.'
        });
      }
    }
    else {
      cacheList.push({
        id: 0,
        key: 'Error Log',
        value: 'No error logs found.'
      });
    }
    this.cacheList = cacheList;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  cacheLogAccordion = {
    header: {
      color: '{text.color}',
      padding: '0.75rem 1rem',
      fontWeight: '400'
    },
    content: {
      background: 'transparent',
      padding: '0'
    },
    colorScheme: {
      light: {
        header: {
          background: '{surface.0}',
          hoverBackground: '{surface.0}',
          activeBackground: '{surface.0}',
          activeHoverBackground: '{surface.0}',
        }
      },
      dark: {
        header: {
          background: '{surface.50}',
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.50}',
          activeHoverBackground: '{surface.50}',
        }
      }
    }
  }
}
